const post = {
  slug: "tip-credit-by-state-2026",
  title: "Tip Credit by State 2026: The Complete Employer Guide",
  description:
    "Every state's 2026 tip credit rate, tipped minimum wage, and the rules employers must follow. Includes all 50 states, D.C., and key localities.",
  date: "2026-05-10",
  category: "Tipped Wages",
  readTime: "12 min read",
  content: `# Tip Credit by State 2026: The Complete Employer Guide

**Last updated: May 2026 | Applies to: All 50 states + D.C.**

---

If you employ tipped workers — servers, bartenders, bussers, delivery drivers — the tip credit is one of the most consequential wage rules you manage. Get it right and you're paying tipped staff legally while keeping labor costs predictable. Get it wrong and you're facing back wages, penalties, and potential DOL audit exposure.

This guide covers every state's 2026 tip credit rules in one place: what the tip credit is, how it works, which states prohibit it entirely, what the current rates are, and the most common mistakes that create liability.

**Want to check your specific situation?** Use our free [Tipped Employee Wage Compliance Checker](/) — enter your state, your pay rates, and employee hours to get an instant compliance verdict.

---

## What is the tip credit?

The tip credit is a federal provision under the Fair Labor Standards Act (FLSA) that allows employers to pay tipped employees a lower cash wage — called the "tipped minimum wage" — as long as the employee's tips make up the difference to reach the applicable minimum wage.

Under federal law, the tip credit works like this:

- **Federal minimum wage:** $7.25/hr
- **Federal tipped minimum (cash wage):** $2.13/hr
- **Maximum tip credit:** $5.12/hr

In plain terms: you can pay a server $2.13/hr in cash if their tips bring their total hourly compensation to at least $7.25. If tips fall short on any given workweek, you must pay the difference — this is called "makeup pay" and it is not optional.

States can set their own minimum wages and tip credit rules. Many have done exactly that, and the variation is significant.

---

## The 8 states (plus D.C.) that prohibit the tip credit entirely

Before diving into state-by-state rates, it's worth flagging the states where the tip credit does not exist. In these jurisdictions, tipped employees must receive the full state minimum wage in cash — tips are extra, on top of wages, not a substitute for them.

| State | 2026 Minimum Wage | Notes |
|---|---|---|
| Alaska | $11.91/hr | No tip credit |
| California | $16.90/hr | No tip credit; major cities are higher |
| Minnesota | $11.13/hr | Large employers ($10M+ revenue); $9.08 for small employers |
| Montana | $10.55/hr | No tip credit |
| Nevada | $12.00/hr | No tip credit |
| Oregon | $15.45/hr | No tip credit; Portland metro is $16.45/hr |
| Washington | $17.13/hr | No tip credit; Seattle is $21.30/hr |
| Washington D.C. | $17.50/hr | Tip credit eliminated via Initiative 82 |

**California note:** Los Angeles is $17.87/hr, San Francisco is $18.67/hr, West Hollywood is $20.25/hr, and Emeryville is $19.36/hr. If you operate in California, always check the local rate in addition to the state rate.

**Washington note:** Seattle's minimum wage is $21.30/hr. Tukwila is $21.65/hr. SeaTac (hospitality and transport workers) is $20.74/hr.

---

## Tip credit rates by state: the full 2026 table

The following rates are effective as of January 1, 2026, unless otherwise noted. Always verify with your state labor department before each payroll, as rates can change mid-year.

| State | State Min Wage | Tipped Cash Wage | Max Tip Credit |
|---|---|---|---|
| Alabama | $7.25 | $2.13 | $5.12 |
| Alaska | $11.91 | $11.91 | None — prohibited |
| Arizona | $14.70 | $11.70 | $3.00 |
| Arkansas | $11.00 | $2.63 | $8.37 |
| California | $16.90 | $16.90 | None — prohibited |
| Colorado | $14.81 | $11.79 | $3.02 |
| Connecticut | $16.94 | $10.71 | $6.23 |
| Delaware | $15.00 | $2.23 | $12.77 |
| Florida | $14.00 | $11.00 | $3.00 |
| Georgia | $7.25 | $2.13 | $5.12 |
| Hawaii | $14.00 | $12.75 | $1.25 |
| Idaho | $7.25 | $3.35 | $3.90 |
| Illinois | $14.00 | $8.40 | $5.60 |
| Indiana | $7.25 | $2.13 | $5.12 |
| Iowa | $7.25 | $4.35 | $2.90 |
| Kansas | $7.25 | $2.13 | $5.12 |
| Kentucky | $7.25 | $2.13 | $5.12 |
| Louisiana | $7.25 | $2.13 | $5.12 |
| Maine | $14.65 | $7.33 | $7.32 |
| Maryland | $15.00 | $3.63 | $11.37 |
| Massachusetts | $15.00 | $7.95 | $7.05 |
| Michigan | $10.56 | $4.01 | $6.55 |
| Minnesota | $11.13 | $11.13 | None — prohibited |
| Mississippi | $7.25 | $2.13 | $5.12 |
| Missouri | $13.75 | $6.88 | $6.88 |
| Montana | $10.55 | $10.55 | None — prohibited |
| Nebraska | $15.00 | $2.13 | $12.87 |
| Nevada | $12.00 | $12.00 | None — prohibited |
| New Hampshire | $7.25 | $3.27 | $3.98 |
| New Jersey | $15.49 | $5.62 | $9.87 |
| New Mexico | $12.00 | $3.00 | $9.00 |
| New York | $16.50 | $13.35 | $3.15 |
| North Carolina | $7.25 | $2.13 | $5.12 |
| North Dakota | $7.25 | $4.86 | $2.39 |
| Ohio | $10.45 | $5.25 | $5.20 |
| Oklahoma | $7.25 | $2.13 | $5.12 |
| Oregon | $15.45 | $15.45 | None — prohibited |
| Pennsylvania | $7.25 | $2.83 | $4.42 |
| Rhode Island | $16.00 | $3.89 | $12.11 |
| South Carolina | $7.25 | $2.13 | $5.12 |
| South Dakota | $11.20 | $5.60 | $5.60 |
| Tennessee | $7.25 | $2.13 | $5.12 |
| Texas | $7.25 | $2.13 | $5.12 |
| Utah | $7.25 | $2.13 | $5.12 |
| Vermont | $14.01 | $7.01 | $7.00 |
| Virginia | $12.41 | $2.13 | $10.28 |
| Washington | $17.13 | $17.13 | None — prohibited |
| Washington D.C. | $17.50 | $17.50 | None — prohibited |
| West Virginia | $8.75 | $2.62 | $6.13 |
| Wisconsin | $7.25 | $2.33 | $4.92 |
| Wyoming | $7.25 | $2.13 | $5.12 |

---

## Key local rates to know

Many cities and counties set minimum wages above the state floor. If you operate in any of these localities, the local rate controls — not the state rate.

### Arizona localities
- **Flagstaff:** $17.85 minimum / $14.85 tipped / $3.00 credit
- **Tucson:** $15.00 minimum / $12.00 tipped / $3.00 credit

### Colorado localities
- **Denver:** $18.29 minimum / $15.27 tipped / $3.02 credit
- **Boulder:** $15.57 minimum / $12.55 tipped / $3.02 credit

### Illinois localities
- **Chicago:** $16.60 minimum / $12.62 tipped / $3.98 credit — *Note: Chicago's tip credit is phasing out entirely by July 1, 2028 under the One Fair Wage ordinance.*
- **Cook County (unincorporated):** $14.05 minimum / $7.80 tipped / $6.25 credit

### Maine localities
- **Portland:** $15.00 minimum / $7.50 tipped / $7.50 credit

### Maryland localities
- **Montgomery County:** $17.15 minimum / $4.00 tipped cash wage
- **Prince George's County:** $16.70 minimum / $4.00 tipped cash wage
- **Howard County:** $16.00 minimum / $4.00 tipped cash wage
- **Baltimore City:** Follows state rate — $15.00 minimum / $3.63 tipped

### New Mexico localities
- **Albuquerque:** $12.00 minimum / $7.20 tipped / $4.80 credit
- **Santa Fe:** $14.60 minimum — no tip credit
- **Las Cruces:** $12.00 minimum / $10.20 tipped / $1.80 credit

### New York localities
- **New York City and Long Island/Westchester:** $16.50 minimum / $13.35 tipped / $3.15 credit
- **Upstate New York:** $15.50 minimum / $12.50 tipped / $3.00 credit

### Washington localities
- **Seattle:** $21.30 minimum — no tip credit
- **Tukwila:** $21.65 minimum — no tip credit
- **SeaTac (hospitality/transport):** $20.74 minimum — no tip credit

---

## How the tip credit actually works: 5 rules every employer must follow

Knowing the rates is half the battle. The other half is understanding the mechanics — the conditions that must be met before any tip credit is valid.

### 1. The cash wage floor is non-negotiable

You must pay the tipped cash wage in cash every pay period, regardless of how many tips the employee receives. Under federal law that floor is $2.13/hr. Under most state laws it's higher. You cannot pay less than the applicable cash wage and "make it up later."

### 2. You must notify employees before taking the credit

The FLSA requires employers to inform tipped employees of the tip credit before claiming it. That notification must include: the cash wage being paid, the tip credit amount being claimed, that tips belong to the employee first and the credit can only be as large as the tips that actually came in (so if tips are short, the credit shrinks and you owe makeup pay), and that all tips belong to the employee (with limited exceptions for valid tip pools). Many states require this in writing and on every pay stub.

### 3. If tips fall short, you pay the difference — no exceptions

If an employee's cash wage plus tips does not equal the applicable minimum wage for any given workweek, you owe makeup pay for that week. This is calculated on a weekly basis under federal law — you cannot average shortfalls across a biweekly or monthly pay period. New Jersey is explicit about this: makeup pay must be calculated on a strict 7-day workweek.

### 4. The 80/20 rule limits tip credit during non-tipped tasks

An employee cannot spend more than 20% of their workweek doing non-tipped side work (rolling silverware, stocking, cleaning, etc.) while being paid at the tipped minimum wage. If they exceed that threshold, you must pay the full minimum wage for those excess hours. This is one of the most frequently cited violations in DOL restaurant audits.

### 5. Tip pools must exclude non-tipped employees (if you take a tip credit)

If you claim a tip credit, only employees who "customarily and regularly" receive tips may participate in a mandatory tip pool — typically servers, bartenders, bussers, and food runners. Back-of-house employees (cooks, dishwashers, prep staff) cannot be included in a mandatory tip pool when a tip credit is in use. Violations here erase the tip credit entirely for affected pay periods.

---

## State-specific rules that trip employers up

### Connecticut
Connecticut requires tipped employees to sign a written attestation each pay period confirming their tips reached minimum wage. Missing this requirement invalidates the tip credit for that entire pay period — even if the employee did receive enough tips. This is a paperwork requirement, not just a wage requirement.

### Florida
Florida's minimum wage increases to $15.00 on September 30, 2026. If you are currently paying at the $14.00 rate with a $3.00 tip credit, your cash wage floor will need to be recalculated when the rate change takes effect. The tipped minimum rises to $11.00 at that point.

### Hawaii
Hawaii's tip credit requires employees to receive tips of at least $7.00/hr above the applicable minimum wage. This threshold is significantly higher than what most states require and can disqualify the credit during slow shifts.

### Michigan
Michigan's tipped wage schedule was updated by SB8 in 2025. Confirm the current effective rate with the Michigan LARA before each payroll — the schedule includes annual step increases.

### New Jersey
New Jersey prohibits employers from deducting credit card processing fees from employee tips. If a customer tips $20 on a credit card and the processing fee is $0.60, you cannot reduce the employee's tip by that amount. The full $20 belongs to the employee.

### Chicago (Illinois)
Chicago's tip credit is on a phase-out schedule under the One Fair Wage ordinance, with full elimination by July 1, 2028. If you operate in Chicago, you should already be planning your payroll structure for the post-tip-credit environment. The 2026 tipped cash wage in Chicago is $12.62/hr.

---

## Upcoming changes to watch in 2026 and beyond

**Florida:** Cash wage floor rises to $11.00/hr on September 30, 2026, when the state minimum hits $15.00.

**Maryland (potential):** Maryland HB 1229/SB 886 (active in the 2026 legislative session) proposes raising the state minimum wage to $25/hr by 2030 and eliminating the tip credit entirely. If enacted, this would fundamentally change tip pooling policies and wage structures for every Maryland hospitality employer. Monitor the Maryland General Assembly for updates.

**Chicago:** Tip credit phase-out continues. Full elimination July 1, 2028.

**Annual rate adjustments:** Arizona, Colorado, Connecticut, Maine, Maryland (counties), Minnesota, Missouri, New Jersey, Ohio, Oregon, Vermont, Virginia, and Washington all adjust minimum wages annually for inflation. These changes typically take effect January 1. Review your rates every December.

---

## How to stay compliant without manually tracking this every year

The honest answer is that tip credit compliance is hard to manage manually, especially if you have multiple locations in different states or counties. The rate changes alone require a December audit every single year, and the calculation rules (makeup pay, the 80/20 test, overtime on tip credit wages) compound the complexity.

The tools that help most:

**Free:** Use the [Tipped Employee Wage Compliance Checker at WorkplacePrompts.ai](/) to run a compliance check for any state and locality, check your 80/20 exposure, and calculate potential back-wage liability before it becomes a problem.

**Payroll automation:** Platforms like Gusto automatically apply updated state and local wage rates, calculate makeup pay weekly, and flag tip credit violations before payroll runs. For employers managing tipped staff across multiple states, this is the most reliable way to stay current without manually tracking every rate change.

---

## Frequently asked questions

**Can I take a tip credit if an employee only works one shift per week?**
Yes, tip credit rules apply per workweek, not per shift. As long as the employee's total weekly cash wages plus tips equal or exceed minimum wage for that week, the credit is valid — but you must still pay the cash wage floor for every hour worked.

**What happens if I don't give advance notice of the tip credit?**
Under the FLSA, failing to provide proper notice before claiming the tip credit invalidates the credit entirely for that period. You would owe the full minimum wage retroactively for all affected hours. Some states have additional notice requirements that carry their own penalties.

**Can I take a tip credit for overtime hours?**
Yes, but the calculation is different. The overtime rate for a tipped employee is 1.5 times the regular minimum wage (not 1.5 times the cash wage). On a $7.25 federal minimum, that's $10.875/hr. You can apply the tip credit to overtime hours, reducing your cash payment, but the tip credit itself does not change.

**Are service charges the same as tips for credit purposes?**
No. Mandatory service charges (such as automatic gratuities on large parties) are not tips — they are wages paid by the employer and must be included in the regular rate of pay for overtime purposes. They do not count as tips for the tip credit calculation.

**Can managers or supervisors receive tips from a pool?**
No. Under the FLSA, supervisors and managers cannot participate in a tip pool — whether or not the employer takes a tip credit. Distributing pooled tips to supervisors invalidates the tip credit and may require payment of back wages for all employees in the pool.

---

## Bottom line

The tip credit is a legitimate wage strategy when applied correctly — but the conditions for correctness are more demanding than many employers realize. The makeup pay obligation, the 80/20 rule, the advance notice requirement, the tip pool restrictions, and the state-by-state variation all create real audit exposure for employers who are managing these manually or relying on outdated rates.

Use the table above as your reference, verify local rates for any city or county where you operate, and build a December rate-check into your annual calendar before January 1 every year.

If you want to check your specific setup — a particular state, your actual pay rates, your current overtime and 80/20 structure — use the free checker at [WorkplacePrompts.ai](/) . It runs all six compliance checks in under a minute and shows your estimated dollar exposure if anything is off.

---

*This post is for informational purposes only and does not constitute legal advice. Wage laws change frequently. Consult a licensed employment attorney before making payroll changes.*

*Some links in this post are affiliate links. We may earn a commission if you sign up for a service through our link, at no additional cost to you.*`,
};

export default post;
